// MIDAS V3 — Confirm dialog (themed alternative to window.confirm)
//
// Replaces the previous `window.confirm("Delete this conversation?")` call in
// `MidasHistoryRailV3`'s delete flow. Chrome's native dialog is dark-themed,
// non-V3, and breaks the visual continuity of the rail (see screenshot
// `wrong.PNG`). MachineID has its own modal style — this matches it.
//
// Architecture mirrors `NewChatDialogV3` so V3 dialogs stay consistent:
//   • Rendered via `ReactDOM.createPortal(..., document.body)` so it always
//     sits above the rail / V3 layout regardless of stacking context.
//   • The portal wrapper has `className="midas-v3-root p-{theme}"` so
//     `var(--midas-v3-*)` token references inside the dialog resolve. The
//     wrapper's own `background: transparent` defeats `.midas-v3-root`'s
//     opaque cream paint (same trap NewChatDialogV3 documents).
//   • Esc key + backdrop click → Cancel (safer default).
//   • Cancel is auto-focused, NOT Confirm — keyboard Enter on first paint
//     does NOT execute the destructive action.
//   • Tab cycles within the dialog (manual focus trap, two buttons + close).
//   • No Radix — plain divs + inline styles. Same primitive set as the rest
//     of V3.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.

import React, {
  useCallback,
  useEffect,
  useRef,
} from "react";
import ReactDOM from "react-dom";

import { resolveV3PaletteClass } from "../../midasV3Palette";

// ============================================================
// Props
// ============================================================

export interface ConfirmDialogV3Props {
  open: boolean;
  /** Heading copy. e.g. "Delete conversation?" */
  title: string;
  /** Body copy. Already-localised, can include inline whitespace + quotes. */
  body: React.ReactNode;
  /** Cancel button label. e.g. "Cancel" / "Zrušiť". */
  cancelLabel: string;
  /** Confirm button label. e.g. "Delete" / "Vymazať". */
  confirmLabel: string;
  /** When true, the confirm button uses destructive (red) styling. */
  destructive?: boolean;
  /** Called when the dialog should close without performing the action. */
  onCancel: () => void;
  /** Called when the user confirms (clicks the confirm button). */
  onConfirm: () => void;
  /** Optional element to restore focus to on close. */
  triggerRef?: React.RefObject<HTMLElement>;
}

// Palette for the body portal: `resolveV3PaletteClass()` (midasV3Palette.ts)
// replaced a local 2-way DOM sniff that fell through to `p-machineid` for any
// palette other than claude — brand-blue dialog on a teal/slate page
// (MIDAS-V3-PALETTES-TEAL-SLATE).

// ============================================================
// Component
// ============================================================

const ConfirmDialogV3: React.FC<ConfirmDialogV3Props> = ({
  open,
  title,
  body,
  cancelLabel,
  confirmLabel,
  destructive = false,
  onCancel,
  onConfirm,
  triggerRef,
}) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);

  // Cache the previous focus target so we can restore on close.
  const cachedTriggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      cachedTriggerRef.current =
        triggerRef?.current ||
        (document.activeElement as HTMLElement | null) ||
        null;
      // Focus the Cancel button — the safer default for a destructive prompt.
      // Defer to next tick so the portal node has mounted.
      const id = window.setTimeout(() => {
        cancelBtnRef.current?.focus();
      }, 0);
      return () => window.clearTimeout(id);
    }
    // Restore focus on close.
    const el = cachedTriggerRef.current;
    if (el && typeof el.focus === "function") {
      el.focus();
    }
    cachedTriggerRef.current = null;
    return undefined;
  }, [open, triggerRef]);

  // Keyboard model: Esc → cancel. Tab cycles within {Cancel, Confirm}.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key === "Tab") {
        const root = dialogRef.current;
        if (!root) return;
        const focusables = Array.from(
          root.querySelectorAll<HTMLElement>(
            'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        ).filter(
          (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [open, onCancel]
  );

  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  if (!open || typeof document === "undefined") return null;

  const theme = resolveV3PaletteClass();

  const node = (
    <div
      className={`midas-v3-root ${theme}`}
      // Same transparent-override pattern as NewChatDialogV3 (see its comment
      // block at lines 547-552 for the root cause). Without this, the wrapper
      // paints the page solid cream and our rgba backdrop just dims the cream.
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1500,
        background: "transparent",
      }}
      role="presentation"
      onMouseDown={(e) => {
        // Backdrop click → Cancel. Only fires on the wrapper itself, not
        // inside the dialog.
        if (e.target === e.currentTarget) {
          onCancel();
        }
      }}
    >
      {/* Backdrop */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(15, 23, 30, 0.32)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
        }}
      />

      {/* Centered dialog */}
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="midas-v3-confirm-title"
        aria-describedby="midas-v3-confirm-body"
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(440px, calc(100vw - 32px))",
          background: "var(--midas-v3-card)",
          border: "1px solid var(--midas-v3-border)",
          borderRadius: 12,
          boxShadow:
            "0 20px 60px rgba(15, 23, 30, 0.35), 0 4px 12px rgba(15, 23, 30, 0.12)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          color: "var(--midas-v3-ink)",
          fontFamily: "var(--font-sans), Sora, system-ui, sans-serif",
        }}
      >
        {/* Body block */}
        <div
          style={{
            padding: "20px 20px 16px",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div
            id="midas-v3-confirm-title"
            style={{
              fontWeight: 600,
              fontSize: 15,
              color: "var(--midas-v3-ink)",
              lineHeight: 1.35,
            }}
          >
            {title}
          </div>
          <div
            id="midas-v3-confirm-body"
            style={{
              fontSize: 13,
              color: "var(--midas-v3-ink-2)",
              lineHeight: 1.5,
            }}
          >
            {body}
          </div>
        </div>

        {/* Action row — Cancel left (ghost, focused), Confirm right
            (destructive when applicable). Matches the proposed UX in the
            task description. */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            padding: "12px 16px 16px",
            background: "transparent",
          }}
        >
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onCancel}
            style={{
              height: 36,
              padding: "0 14px",
              borderRadius: 7,
              background: "transparent",
              border: "1px solid var(--midas-v3-border)",
              color: "var(--midas-v3-ink-2)",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              transition:
                "background-color .12s, border-color .12s, color .12s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--midas-v3-card-2)";
              e.currentTarget.style.color = "var(--midas-v3-ink)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--midas-v3-ink-2)";
            }}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={onConfirm}
            style={{
              height: 36,
              padding: "0 14px",
              borderRadius: 7,
              background: destructive ? "#dc2626" : "var(--midas-v3-accent)",
              border: destructive
                ? "1px solid #dc2626"
                : "1px solid var(--midas-v3-accent)",
              color: "#fff",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              transition: "background-color .12s, border-color .12s",
            }}
            onMouseEnter={(e) => {
              if (destructive) {
                e.currentTarget.style.background = "#b91c1c";
                e.currentTarget.style.borderColor = "#b91c1c";
              } else {
                e.currentTarget.style.background = "var(--midas-v3-accent-hov)";
                e.currentTarget.style.borderColor = "var(--midas-v3-accent-hov)";
              }
            }}
            onMouseLeave={(e) => {
              if (destructive) {
                e.currentTarget.style.background = "#dc2626";
                e.currentTarget.style.borderColor = "#dc2626";
              } else {
                e.currentTarget.style.background = "var(--midas-v3-accent)";
                e.currentTarget.style.borderColor = "var(--midas-v3-accent)";
              }
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
};

export default ConfirmDialogV3;
